"use client";
import React from "react";
import {
  useEffect,
  useState,
  useAppDispatch,
  useAppSelector,
  useRef,
} from "@/utils/hooks/reactUseCustomHooks";
import {
  Box,
  Text,
  DropdownContent,
  DropdownMenu,
  DropdownTrigger,
  Button,
} from "@dxp-feed/ui";
import { getGeolocation } from "@dxp-feed/store-locator";
import {
  getFromLocalStorage,
  saveToLocalStorage,
  setStoreCookies,
} from "@/helpers/storageHelper";
import {
  useDebounce,
  getStoreDetailByStoreNumber,
  constructAllFilterQuery,
  getStoreStatusWithRange,
} from "@/helpers/storeLocatorHelper";
import {
  IListStores,
  setOtherStoreInfo,
  setSelectedStore,
  setStoreAemData,
} from "@/utils/redux/storeLocator/index";
import { RootState } from "@/utils/redux/store";
import UpdateStoreModalHeader from "@/components/Header/StoreLocator/UpdateStoreModalHeader";
import StoreSelector from "@/components/Header/StoreLocator/StoreSelector";
import HeaderDropdown from "@/components/Header/StoreLocator/HeaderDropdown";
import { StoreData } from "@/utils/types/storeLocator";
import { useUpdateStore } from "@/utils/hooks/useUpdateStore";
import { ALGOLIA_INDEXES } from "@/services/algolia/config";
import { updateBannerRegion } from "@/services/newRelic";
import { algolia } from "@/services/algolia";
import { memo } from "react";
import { setAlert } from "@/utils/redux/gigya/scene";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { ALGOLIA_ANALYTICS_TAGS } from "@/utils/constant";
import { setCookie } from "cookies-next";

const MemoizedStoreSelector = memo(StoreSelector);
const MemoizedHeaderDropdown = memo(HeaderDropdown);
const MemoizedUpdateStoreModalHeader = memo(UpdateStoreModalHeader);

export const StoreLocator = ({storeLocatorData}:any) => {
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { updateStoreIfAuthorized } = useUpdateStore();

  const isDropdownOpen = useAppSelector(
    (state) => state.otherStoreInfo.isDropdownOpen
  );
  const storeAEMData = storeLocatorData?.data?.storeLocatorModelList?.items?.[0];
  dispatch(setStoreAemData({ storeAemData: storeAEMData }));
  const [listStores, setListStore] = useState<IListStores[]>([]);
  const selectedStore = useAppSelector(
    (state: RootState) => state.selectedStore.data as IListStores
  );
  const userSession = useAppSelector((state) => state.gigyaslice);
  const [loading, setLoading] = useState<boolean>(true);
  const [confrimLoading, setConfrimLoading] = useState(false);
  const [geolocationError, setGeolocationError] = useState<string | null>(null);
  const [searchKey, setSearchKey] = useState<string>("");
  const [localSearchKey, setLocalSearchKey] = useState("");
  const [isConfirmationModalOpen, setConfirmationModalOpen] =
    useState<boolean>(false);
  const [storeToConfirm, setStoreToConfirm] = useState<IListStores | null>(
    null
  );
  const [searchLoading, setSearchLoading] = useState(false);
  const [tempStore, setTempStore] = useState<any | null>(null);

  const setBrowserData = async (storeData: any) => {
    saveToLocalStorage("store", storeData);
    saveToLocalStorage("storeRegion", storeData?.region?.[0]);
    dispatch(setSelectedStore(storeData as IListStores));
    setStoreCookies(storeData?.storeNumber, storeData?.region?.[0]);
    setCookie("province", storeData?.address?.province);
  };

  const handleSearch = useDebounce((query: string) => {
    setSearchLoading(true);
    setSearchKey(query);
  }, 700);

  const onHandleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setLocalSearchKey(value);
    handleSearch(value);
  };

  useEffect(() => {
    (async () => {
      const newStoreNumber = searchParams.get("set_preferred_store_number");
      if (newStoreNumber && pathName.includes("/flyer")) {
        const completeStoreNumber = newStoreNumber?.padStart(4, "0");
        const storeDetails =
          await getStoreDetailByStoreNumber(completeStoreNumber);
        if (storeDetails) {
          await setBrowserData(storeDetails);
          if (userSession?.uid) {
            await updateStoreIfAuthorized(storeDetails?.storeNumber);
          }
          updateBannerRegion("success", process.env.NEXT_PUBLIC_DEFAULT_BANNER);
          //removing set_preferred_store_number from route
          const newSearchParams = new URLSearchParams(searchParams.toString());
          newSearchParams.delete("set_preferred_store_number");
          const newUrl = `${pathName}?${newSearchParams.toString()}`;
          router.push(newUrl, undefined);
        } else {
          dispatch(
            setAlert({
              message: "Something Went Wrong while updating preferred store.",
              color: "error",
              isError: true,
            })
          );
        }
      }
    })()
      .then(() => {})
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, pathName]);

  const fetchStoreData = async (params: any = {}) => {
    try {
      const userLocation = getFromLocalStorage("userLocation") as {
        latitude: number;
        longitude: number;
      };

      const lat =
        params.lat ||
        userLocation?.latitude ||
        selectedStore?._geoloc?.lat ||
        0;
      const lng =
        params.lng ||
        userLocation?.longitude ||
        selectedStore?._geoloc?.lng ||
        0;

      const data = await algolia.storeSearchByLatLng({
        lat,
        lng,
        query: searchKey || "",
        indexName: ALGOLIA_INDEXES.STORES.PRIMARY_INDEX,
        hitsPerPage: 4,
        page: params.page,
        filters: constructAllFilterQuery([], [], []),
        facets: ["departments.type", "address.province"],
        analyticsTags: ALGOLIA_ANALYTICS_TAGS,
      } as any);

      const fetchedStores = data?.hits || [];
      const storedStore = getFromLocalStorage("store") as IListStores;

      let reorderedStores: IListStores[] = [];

      if (!searchKey) {
        if (storedStore) {
          reorderedStores = [
            storedStore,
            ...(fetchedStores as IListStores[]).filter(
              (store: IListStores) =>
                store.storeNumber !== storedStore.storeNumber
            ),
          ];
        } else {
          reorderedStores = fetchedStores as IListStores[];
        }
      } else {
        if (fetchedStores.length > 0) {
          if (storedStore) {
            const isStoredStoreInFetched = fetchedStores.some(
              (store: StoreData) =>
                store.storeNumber === storedStore.storeNumber
            );

            if (isStoredStoreInFetched) {
              reorderedStores = [
                storedStore,
                ...(fetchedStores as IListStores[]).filter(
                  (store: IListStores) =>
                    store.storeNumber !== storedStore.storeNumber
                ),
              ];
            } else {
              reorderedStores = fetchedStores as IListStores[];
            }
          } else {
            reorderedStores = fetchedStores as IListStores[];
          }
        } else {
          reorderedStores = [];
        }
      }
      setListStore(reorderedStores);
      if ((params?.geolocationDefault || !storedStore) && !searchKey && fetchedStores.length > 0) {
        const firstStore = fetchedStores[0] as IListStores;
        await setBrowserData(firstStore);
      } else if (storedStore) {
        setStoreCookies(storedStore?.storeNumber, storedStore?.region?.[0]);
        setCookie("province", storedStore?.address?.province);
      }
    } catch (error) {
      setGeolocationError("Failed to fetch store data");
      console.log("Error during store data fetch:", error);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  //use effect show default store in header
  useEffect(() => {
    const getDefaultStoreForTemp = async () => {
      const tempStoreData = await getStoreDetailByStoreNumber(
        process.env.NEXT_PUBLIC_DEFAULT_STORE_NUMBER
      );
      setTempStore(tempStoreData);
      await setBrowserData(tempStoreData);
      setLoading(false);
    };
    const store = getFromLocalStorage("store");
    if (!store) {
      getDefaultStoreForTemp();
    }
  }, []);

  //logged in user store selection
  useEffect(() => {
    if (userSession?.sobeysCom?.storeId) {
      const getstoreDetaiWhenuserLoggedIn = async () => {
        const sessionStoreData = await getStoreDetailByStoreNumber(
          userSession?.sobeysCom?.storeId
        );
        await setBrowserData(sessionStoreData);
        await fetchStoreData({
          lat: sessionStoreData?._geoloc?.lat,
          lng: sessionStoreData?._geoloc?.lng,
        });
      };
      void getstoreDetaiWhenuserLoggedIn();
    }
  }, [userSession?.uid, userSession.loggedIn]);

  useEffect(() => {
    const initializeStoreSelection = async () => {
      const storedStore: IListStores = getFromLocalStorage(
        "store"
      ) as IListStores;
      const geolocationHandled: string = getFromLocalStorage(
        "geolocationHandled"
      ) as string;
      const defaultStoreNumber =
        process.env.NEXT_PUBLIC_DEFAULT_STORE_NUMBER || "";

      const setDefaultAndFetch = async () => {
        const defaultStore =
          await getStoreDetailByStoreNumber(defaultStoreNumber);
        if (defaultStore) {
          await setBrowserData(defaultStore);
          await fetchStoreData({
            lat: defaultStore._geoloc.lat,
            lng: defaultStore._geoloc.lng,
          });
        } else {
          setGeolocationError(
            "Could not load a default store. Please try again."
          );
          dispatch(
            setAlert({
              message: "Could not load a default store. Please try again.",
              color: "error",
              isError: true,
            })
          );
        }
      };

      if (storedStore && Object.keys(selectedStore).length === 0) {
        await setBrowserData(storedStore);
        await fetchStoreData({
          lat: storedStore._geoloc.lat,
          lng: storedStore._geoloc.lng,
        });
        setLoading(false);
        return;
      }
      if (!geolocationHandled) {
        try {
          const geoData = await getGeolocation();
          saveToLocalStorage("geolocationHandled", "true");
          saveToLocalStorage("userLocation", geoData);
          setGeolocationError(null);
          await fetchStoreData({
            lat: geoData.latitude,
            lng: geoData.longitude,
            geolocationDefault: true,
          });
        } catch (err: any) {
          if (typeof err === "object" && err !== null && "code" in err) {
            const errorWithCode = err as { code: number };
            if (errorWithCode.code === 1) {
              console.warn(
                "Geolocation permission denied. Falling back to default store."
              );
            } else {
              setGeolocationError(
                err.message || "Failed to fetch geolocation."
              );
              dispatch(
                setAlert({
                  message:
                    "Failed to get your location. Please try searching for a store.",
                  color: "error",
                  isError: true,
                })
              );
            }
          } else if (err instanceof Error) {
            setGeolocationError(
              err.message || "An unknown error occurred during geolocation."
            );
            dispatch(
              setAlert({
                message:
                  "Failed to get your location. Please try searching for a store.",
                color: "error",
                isError: true,
              })
            );
          } else {
            setGeolocationError(
              "An unknown error occurred during geolocation."
            );
            dispatch(
              setAlert({
                message:
                  "Failed to get your location. Please try searching for a store.",
                color: "error",
                isError: true,
              })
            );
          }
          saveToLocalStorage("geolocationHandled", "true");
          await setDefaultAndFetch();
        }
      } else {
        const userLocation = getFromLocalStorage("userLocation") as {
          latitude: number;
          longitude: number;
        };
        if (
          userLocation &&
          userLocation.latitude !== 0 &&
          userLocation.longitude !== 0
        ) {
          await fetchStoreData();
        } else {
          if (!selectedStore || Object.keys(selectedStore).length === 0) {
            await setDefaultAndFetch();
          } else {
            await fetchStoreData();
          }
        }
      }
      setLoading(false);
    };

    void initializeStoreSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]); // Re-run when user session or searchKey changes

  const hasMounted = useRef(false);
  useEffect(() => {
    hasMounted.current = true;
  }, []);

  useEffect(() => {
    if (
      !selectedStore ||
      !selectedStore._geoloc?.lat ||
      !selectedStore._geoloc?.lng
    ) {
      return;
    }

    const params = {
      lat: selectedStore._geoloc.lat,
      lng: selectedStore._geoloc.lng,
    };
    void fetchStoreData(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore]);

  useEffect(() => {
    if (hasMounted.current && searchKey?.length) {
      void fetchStoreData();
    }
  }, [searchKey]);

  // add useEffect for the stop scrolling body(for mobile) when pop up modal open
  useEffect(() => {
    if (isConfirmationModalOpen || isDropdownOpen) {
      document.body.classList.add("stop-scroll");
    } else {
      document.body.classList.remove("stop-scroll");
    }
  }, [isConfirmationModalOpen, isDropdownOpen]);

  useEffect(() => {
    const handler = () => {
      dispatch(setOtherStoreInfo({ isDropdownOpen: false }));
    };
    window.addEventListener("guide-activated", handler);
    return () => window.removeEventListener("guide-activated", handler);
  }, []);

  if (geolocationError) {
    return (
      <Text as="span" className="text-white">
        {geolocationError}
      </Text>
    );
  }

  const handleStoreSelection = (storeNumber: number): void => {
    const store = listStores.find(
      (item: IListStores) => item.storeNumber === storeNumber
    );
    if (store) {
      setStoreToConfirm(store);
      setConfirmationModalOpen(true);
    }
  };

  const confirmStoreSelection = async () => {
    if (storeToConfirm) {
      setConfrimLoading(true);
      await setBrowserData(storeToConfirm);
      const updatedStoresData = [
        storeToConfirm,
        ...listStores.filter(
          (store: IListStores) =>
            store.storeNumber !== storeToConfirm.storeNumber
        ),
      ];
      setListStore(updatedStoresData);
      setSearchKey("");
      setLocalSearchKey("");
      if (userSession?.uid) {
        await updateStoreIfAuthorized(storeToConfirm?.storeNumber);
      }
      updateBannerRegion("success", process.env.NEXT_PUBLIC_DEFAULT_BANNER);
      setConfrimLoading(false);
    }
    closeDropDownTrigger();
    dispatch(setOtherStoreInfo({ isDropdownOpen: false }));
  };

  const cancelStoreSelection = (): void => {
    setStoreToConfirm(null);
    dispatch(setOtherStoreInfo({ isDropdownOpen: false }));
    setSearchKey("");
    setLocalSearchKey("");
    closeDropDownTrigger();
  };

  const handleCloseDropDown = () => {
    dispatch(setOtherStoreInfo({ isDropdownOpen: false }));
    setSearchKey("");
    setLocalSearchKey("");
    closeDropDownTrigger();
  };

  const closeDropDownTrigger = (): void => {
    setConfirmationModalOpen(false);
  };

  const storeFromLocal = getFromLocalStorage("store");
  const retrievedData: any | null = storeFromLocal || tempStore;
  const hours = retrievedData?.hours ?? null;
  const is24Hours = hours?.is24Hours ?? false;
  const storeHours: any = hours?.storeHours ?? null;
  const storeStatus = is24Hours
    ? "Open 24 Hours"
    : getStoreStatusWithRange(storeHours, retrievedData?.address?.province);

  return (
    <Box
      as="div"
      id="store_locator"
      className="lg:border-l-[1px] border-solid border-l-brandWhite pl-[30px] lg:pl-[38px] 2lg:pl-[50px] text-brandWhite flex justify-center flex-col relative flex-grow basis-0 max-w-auto lg:w-[250px] 2lg:w-[280px]"
      onBlur={(e: any) => {
        const currentTarget = e.currentTarget;
        const relatedTarget = e.relatedTarget;
        if (!relatedTarget) return;
        if (!currentTarget?.contains(relatedTarget)) {
          dispatch(setOtherStoreInfo({ isDropdownOpen: false }));
        }
      }}
    >
      <DropdownMenu
        closeOnItemClick={false}
        controlledOpen={isDropdownOpen}
        onClose={handleCloseDropDown}
      >
        <DropdownTrigger className="text-left relative" asChild>
          <Box
            as="div"
            onClick={() =>
              dispatch(setOtherStoreInfo({ isDropdownOpen: !isDropdownOpen }))
            }
            onKeyDown={(e: any) => {
              if (e.key === "Enter" || e.key === " ") {
                dispatch(
                  setOtherStoreInfo({ isDropdownOpen: !isDropdownOpen })
                );
              }
            }}
          >
            <MemoizedHeaderDropdown
              selectedStore={storeFromLocal || tempStore}
              loading={loading}
              storeStatus={storeStatus}
              geolocationError={geolocationError}
            />
          </Box>
        </DropdownTrigger>
        <DropdownContent className="!pr-0 mt-0 p-0 sm:w-[480px] h-svh sm:h-auto sm:absolute fixed w-full rounded-none sm:-left-[30px] lg:-left-6 top-0 sm:top-[67px] md:top-[70px] lg:top-[69px]">
          <Box className="flex justify-between items-center 4xs:p-6 px-6 py-4 border-b-2 border-solid border-grey350 mb-3 sm:hidden">
            <Text as="p" className="text-base text-brandBlack font-bold">
              {storeToConfirm ? "Update Selected Store" : "Select Your Store"}
            </Text>
            <Button
              variant="icon"
              className="p-0"
              aria-label="Close Icon"
              onClick={() => {
                cancelStoreSelection();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  dispatch(
                    setOtherStoreInfo({ isDropdownOpen: !isDropdownOpen })
                  );
                }
              }}
            >
              <Text as="span" className="icon-close text-brandBlack " />
            </Button>
          </Box>
          <Box>
            {isDropdownOpen && !isConfirmationModalOpen && (
              <MemoizedStoreSelector
                localSearchKey={localSearchKey}
                searchKey={searchKey}
                listStores={listStores}
                selectedStore={selectedStore}
                setLocalSearchKey={setLocalSearchKey}
                setSearchKey={setSearchKey}
                setConfirmationModalOpen={setConfirmationModalOpen}
                handleStoreSelection={handleStoreSelection}
                onHandleSearch={onHandleSearch}
                onItemClickHandler={() =>
                  dispatch(setOtherStoreInfo({ isDropdownOpen: false }))
                }
                searchLoading={searchLoading}
              />
            )}
            {isConfirmationModalOpen && (
              <MemoizedUpdateStoreModalHeader
                confirmStoreSelection={confirmStoreSelection}
                cancelStoreSelection={cancelStoreSelection}
                confrimLoading={confrimLoading}
                storeAEMData={storeAEMData}
              />
            )}
          </Box>
        </DropdownContent>
      </DropdownMenu>
    </Box>
  );
};
